// @flow

import { of, empty } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import {
  TASK_INSTANCE_COMPLETED_STATUS,
  TASK_INSTANCE_PENDING_STATUS
} from 'sling-calendar-sdk/constants';

import type { ActionsObservable, StateObservable, Middleware } from '../common/typings';
import { addSuccessToast, addErrorToast } from '../utils/actions';

import updateSubtaskStatus from './helpers/updateSubtaskStatus';
import shouldCompleteTask from './helpers/shouldCompleteTask';
import {
  UPDATE_SUBTASK_STATUS,
  COMPLETE_TASK_INSTANCE,
  REOPEN_TASK_INSTANCE,
  completeTaskInstance,
  updateTaskInstanceSuccess
} from './actions';

export function updateSubtaskStatusEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { SlingApi }: Middleware
) {
  return action$.pipe(
    ofType(UPDATE_SUBTASK_STATUS),
    switchMap(action => {
      const { taskInstance, subtaskId, status } = action.payload;
      return SlingApi.updateTaskInstanceSubtaskStatus(subtaskId, status).pipe(
        switchMap(() => {
          const updatedTask = updateSubtaskStatus(taskInstance, subtaskId, status);

          if (shouldCompleteTask(taskInstance, subtaskId, status)) {
            return of(completeTaskInstance(updatedTask));
          }

          return of(updateTaskInstanceSuccess(updatedTask));
        }),
        catchError(error => {
          if (error?.message === '401') {
            return of(addErrorToast('Shift task feature is only available in business plan.'));
          }
          return of(addErrorToast(error));
        })
      );
    })
  );
}

export function completeTaskInstanceEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { SlingApi }: Middleware
) {
  return action$.pipe(
    ofType(COMPLETE_TASK_INSTANCE),
    switchMap(action => {
      const { taskInstance } = action.payload;
      if (taskInstance.status === TASK_INSTANCE_COMPLETED_STATUS) {
        return empty();
      }

      return SlingApi.updateTaskInstanceStatus(
        taskInstance.id,
        TASK_INSTANCE_COMPLETED_STATUS
      ).pipe(
        switchMap(() => {
          const updatedTask = {
            ...taskInstance,
            status: TASK_INSTANCE_COMPLETED_STATUS
          };
          return [
            addSuccessToast('Task has been marked as completed'),
            updateTaskInstanceSuccess(updatedTask)
          ];
        }),
        catchError(error => of(addErrorToast(error)))
      );
    })
  );
}

export function reopenTaskInstanceEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { SlingApi }: Middleware
) {
  return action$.pipe(
    ofType(REOPEN_TASK_INSTANCE),
    switchMap(action => {
      const { taskInstance } = action.payload;

      return SlingApi.updateTaskInstanceStatus(taskInstance.id, TASK_INSTANCE_PENDING_STATUS).pipe(
        switchMap(() => {
          const updatedTask = {
            ...taskInstance,
            status: TASK_INSTANCE_PENDING_STATUS
          };
          return [
            addSuccessToast('Task has been successfully reopened'),
            updateTaskInstanceSuccess(updatedTask)
          ];
        }),
        catchError(error => of(addErrorToast(error)))
      );
    })
  );
}

export default combineEpics(
  updateSubtaskStatusEpic,
  completeTaskInstanceEpic,
  reopenTaskInstanceEpic
);
